import React from "react";
import type { Theme } from "@material-ui/core";
import { Box, Fade, makeStyles, MenuItem as Item, useMediaQuery, withStyles } from "@material-ui/core";
import Menu from "@material-ui/core/Menu";
import clsx from "clsx";

import { ArrowRight as ArrowRightIcon } from "@eroad/icons/material";

import type { CoretexTheme } from "../theme";
import type { ContextMenuItem, Coord } from "./types";

const useStyles = makeStyles((theme: CoretexTheme) => ({
  root: {
    borderRadius: "10px",
    boxShadow: theme.elevation.standard,
    padding: theme.spacing(2),
  },
  submenu: {
    position: "absolute",
    right: "0px",
  },
  mobileMenu: {
    position: "absolute",
    bottom: "0!important" as const,
    left: "0!important" as const,
    right: "0!important" as const,
    top: "auto!important" as const,
    width: "100%",
    maxWidth: "100%",
  },
}));

const MenuItem = withStyles((theme: Theme) => ({
  root: {
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(Item);

const initialState = {
  mouseX: null,
  mouseY: null,
};

export interface ContextMenuProps {
  menuItems: ContextMenuItem[];
  coords?: Coord;
}

const ContextMenu = ({ menuItems, coords }: ContextMenuProps): JSX.Element => {
  const classes = useStyles();
  const isSmall = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const [state, setState] = React.useState<{
    mouseX: null | number;
    mouseY: null | number;
  }>(initialState);

  React.useEffect(
    () =>
      setState({
        mouseX: coords?.x ?? null,
        mouseY: coords?.y ?? null,
      }),
    [coords],
  );
  const onClose = () => {
    setState(initialState);
  };
  return (
    <Menu
      keepMounted
      open={state.mouseY !== null}
      onClose={onClose}
      TransitionComponent={Fade}
      anchorReference="anchorPosition"
      data-testid="ctxMenu"
      classes={{ paper: clsx(classes.root, isSmall && classes.mobileMenu) }}
      anchorPosition={
        state.mouseY !== null && state.mouseX !== null ? { top: state.mouseY, left: state.mouseX } : undefined
      }
    >
      {menuItems.map((menuItem: ContextMenuItem) => (
        <MenuItem
          key={`ctx-${menuItem.title}`}
          onClick={() => {
            if (menuItem.action) menuItem.action();
            if (!menuItem.subMenu) onClose();
          }}
          dense
        >
          {menuItem.title}
          {menuItem.subMenu && (
            <>
              <ArrowRightIcon className={classes.submenu} />
              <Box>
                <Menu open={false}>
                  {menuItem.subMenu.map((subItem) => (
                    <MenuItem key={`sub-${subItem.title}`}>{subItem.title}</MenuItem>
                  ))}
                </Menu>
              </Box>
            </>
          )}
        </MenuItem>
      ))}
    </Menu>
  );
};

export default ContextMenu;
